import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronDown, 
  ChevronUp, 
  GitBranch,
  User,
  MapPin,
  Calendar,
  Eye,
  X,
  Loader2,
  Link2,
  Shield,
  Info,
  Lock,
  Sparkles,
  AlertCircle,
  BookmarkPlus,
  CheckCircle2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Match type descriptions - calm investigative tone
const matchTypeInfo = {
  exact_name_and_year: {
    label: 'Strong Match',
    description: 'Name and birth year align closely'
  },
  name_and_location: {
    label: 'Regional Connection',
    description: 'Shared name and geographic origin'
  },
  name_match: {
    label: 'Name Similarity',
    description: 'Names show potential connection'
  },
  surname_cluster: {
    label: 'Surname Pattern',
    description: 'Part of a broader surname cluster'
  },
  combined: {
    label: 'Combined Signals',
    description: 'Multiple factors suggest this connection'
  },
  birth_year_proximity: {
    label: 'Time Period Match',
    description: 'Birth years align closely'
  },
  location_overlap: {
    label: 'Location Match',
    description: 'Shared geographic region'
  },
  name_similarity: {
    label: 'Name Similarity',
    description: 'Names show potential connection'
  },
  default: {
    label: 'Potential Connection',
    description: 'Algorithmic similarity detected'
  }
};

// Confidence tiers (aligned with DNA insights)
const confidenceConfig = {
  strong: {
    label: 'Strong indication',
    description: 'Multiple signals suggest this connection',
    barWidth: '100%',
    barColor: 'bg-primary',
    badge: 'bg-primary/10 text-primary border-primary/30'
  },
  likely: {
    label: 'Likely connection',
    description: 'Signals may indicate this connection',
    barWidth: '66%',
    barColor: 'bg-primary/80',
    badge: 'bg-primary/5 text-primary border-primary/20'
  },
  possible: {
    label: 'Possible connection',
    description: 'Further research may clarify',
    barWidth: '33%',
    barColor: 'bg-muted-foreground/50',
    badge: 'bg-muted/60 text-muted-foreground border-border'
  }
};

// Match score indicator - uses confidence tier when available (DNA-style)
const MatchScoreIndicator = ({ score, confidenceTier }) => {
  const tier = confidenceTier && confidenceConfig[confidenceTier] ? confidenceTier : null;
  const config = tier ? confidenceConfig[tier] : null;
  
  const percentage = Math.round(score);
  let strengthLabel = 'Low';
  let barWidth = '25%';
  let barColor = 'bg-muted-foreground';
  
  if (config) {
    strengthLabel = config.label;
    barWidth = config.barWidth;
    barColor = config.barColor;
  } else if (percentage >= 80) {
    strengthLabel = 'Strong';
    barWidth = '100%';
    barColor = 'bg-muted-foreground';
  } else if (percentage >= 60) {
    strengthLabel = 'Moderate';
    barWidth = '75%';
    barColor = 'bg-muted-foreground';
  } else if (percentage >= 40) {
    strengthLabel = 'Possible';
    barWidth = '50%';
    barColor = 'bg-muted-foreground';
  }
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wider">Match Strength</span>
        <span className={`text-[10px] ${tier ? `px-1.5 py-0.5 rounded border ${config.badge}` : 'text-muted-foreground'}`}>
          {strengthLabel}
        </span>
      </div>
      <div className="h-1 bg-secondary rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} rounded-full transition-all duration-500`}
          style={{ width: barWidth }}
        />
      </div>
    </div>
  );
};

// Shared signals display (surname, location, relatives, time overlap)
const SharedSignalsDisplay = ({ signals }) => {
  if (!signals || !Array.isArray(signals) || signals.length === 0) return null;
  
  const iconMap = {
    'Surname similarity': <User className="w-3 h-3" />,
    'Name match': <User className="w-3 h-3" />,
    'Time period overlap': <Calendar className="w-3 h-3" />,
    'Location overlap': <MapPin className="w-3 h-3" />,
    'Algorithmic similarity': <Info className="w-3 h-3" />
  };
  
  return (
    <div className="flex flex-wrap gap-1.5">
      {signals.map((signal) => (
        <span 
          key={signal}
          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border"
        >
          {iconMap[signal] || <Info className="w-3 h-3" />}
          {signal}
        </span>
      ))}
    </div>
  );
};

// Score breakdown for transparency
const ScoreBreakdown = ({ breakdown }) => {
  if (!breakdown || typeof breakdown !== 'object') return null;
  
  const labels = {
    name_similarity: 'Name',
    birth_year_proximity: 'Birth year',
    location_overlap: 'Location',
    surname_cluster: 'Surname'
  };
  
  const entries = Object.entries(breakdown).filter(([, v]) => v != null && v > 0);
  if (entries.length === 0) return null;
  
  return (
    <div className="space-y-1.5">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground/80 w-24">{labels[key] || key}</span>
          <div className="flex-1 h-1 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full bg-muted-foreground/60 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(100, value)}%` }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground w-6">{Math.round(value)}</span>
        </div>
      ))}
    </div>
  );
};

// Match factors display (supports legacy and new score_breakdown format)
const MatchFactors = ({ factors }) => {
  if (!factors || Object.keys(factors).length === 0) return null;
  
  const factorIcons = {
    name: <User className="w-3 h-3" />,
    surname: <User className="w-3 h-3" />,
    name_similarity: <User className="w-3 h-3" />,
    surname_cluster: <User className="w-3 h-3" />,
    year: <Calendar className="w-3 h-3" />,
    birth_year: <Calendar className="w-3 h-3" />,
    birth_year_proximity: <Calendar className="w-3 h-3" />,
    location: <MapPin className="w-3 h-3" />,
    birth_location: <MapPin className="w-3 h-3" />,
    location_overlap: <MapPin className="w-3 h-3" />
  };
  
  const factorLabels = {
    name: 'Name',
    surname: 'Surname',
    name_similarity: 'Name similarity',
    surname_cluster: 'Surname cluster',
    given_name: 'Given Name',
    year: 'Year',
    birth_year: 'Birth Year',
    birth_year_proximity: 'Birth year',
    location: 'Location',
    birth_location: 'Birth Place',
    location_overlap: 'Location',
    source_name: 'Your ancestor',
    source_birth_year: 'Birth year',
    source_location: 'Location'
  };
  
  return (
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(factors).map(([key, value]) => {
        if (value == null || value === '' || (typeof value === 'number' && value === 0)) return null;
        if (key.startsWith('target_')) return null;
        return (
          <span 
            key={key}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border"
          >
            {factorIcons[key] || <Info className="w-3 h-3" />}
            {factorLabels[key] || key}
            {typeof value === 'number' ? ` ${Math.round(value)}` : ''}
          </span>
        );
      })}
    </div>
  );
};

// Single Match Card
const MatchCard = ({ match, treeId, treeName, onViewSummary, onDismiss, onAcknowledge, onRequestConnection, onSaveToJournal, isProcessing, isSavingToJournal }) => {
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  
  const typeInfo = matchTypeInfo[match.match_type] || matchTypeInfo.default;
  
  const handleConfirmDismiss = () => {
    onDismiss(match.candidate_id);
    setShowDismissDialog(false);
  };
  
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        <Card className="bg-card border-secondary hover:border-border transition-colors">
          <CardContent className="p-3 space-y-3">
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                  <GitBranch className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground/90">{match.your_person_name}</p>
                  <p className="text-[10px] text-muted-foreground/60">{typeInfo.label}</p>
                </div>
              </div>
            </div>
            
            {/* Privacy-safe description */}
            <div className="flex items-start gap-2 p-2 rounded-lg bg-secondary border border-border">
              <Shield className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-[11px] text-muted-foreground/80 leading-relaxed italic">
                {match.privacy_label}
              </p>
            </div>
            
            {/* Shared signals */}
            {match.shared_signals && match.shared_signals.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Shared Signals</p>
                <SharedSignalsDisplay signals={match.shared_signals} />
              </div>
            )}
            
            {/* Match factors (fallback) */}
            {match.match_factors && Object.keys(match.match_factors).length > 0 && !match.shared_signals?.length && (
              <div className="space-y-1.5">
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Shared Characteristics</p>
                <MatchFactors factors={match.match_factors} />
              </div>
            )}
            
            {/* Match score with confidence tier */}
            <MatchScoreIndicator score={match.match_score} confidenceTier={match.confidence_tier} />
            
            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 pt-1">
              {onAcknowledge && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAcknowledge(match.candidate_id)}
                  disabled={isProcessing}
                  className="h-7 px-2 text-[10px]"
                  data-testid={`acknowledge-overlap-${match.candidate_id}`}
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Mark reviewed
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewSummary(match)}
                disabled={isProcessing}
                className="flex-1 h-7 text-xs border-border text-foreground hover:bg-secondary"
                data-testid={`view-match-btn-${match.candidate_id}`}
              >
                <Eye className="w-3 h-3 mr-1.5" />
                View Summary
              </Button>
              {match.connection_request_sent ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled
                  className="flex-1 h-7 text-xs border-border text-muted-foreground"
                  data-testid={`request-sent-${match.candidate_id}`}
                >
                  <CheckCircle2 className="w-3 h-3 mr-1.5" />
                  Request Sent
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRequestConnection(match)}
                  disabled={isProcessing}
                  className="flex-1 h-7 text-xs border-border text-foreground hover:bg-secondary"
                  data-testid={`request-connection-btn-${match.candidate_id}`}
                >
                  <Link2 className="w-3 h-3 mr-1.5" />
                  Request Connection
                </Button>
              )}
              {onSaveToJournal && treeId && treeName && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSaveToJournal(match)}
                  disabled={isProcessing || isSavingToJournal}
                  className="h-7 px-2 text-[10px] border-border text-muted-foreground hover:text-foreground"
                  data-testid={`save-discovery-to-journal-${match.candidate_id}`}
                >
                  {isSavingToJournal ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookmarkPlus className="w-3 h-3 mr-1" />}
                  Add to Journal
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDismissDialog(true)}
                disabled={isProcessing}
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary"
                data-testid={`dismiss-match-btn-${match.candidate_id}`}
                title="Dismiss this match"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      
      {/* Dismiss confirmation dialog */}
      <Dialog open={showDismissDialog} onOpenChange={setShowDismissDialog}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Dismiss This Match?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This potential connection will be removed from your matches list.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary border border-border">
              <GitBranch className="w-5 h-5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">{match.your_person_name}</p>
                <p className="text-xs text-muted-foreground">{typeInfo.label} • {typeInfo.description}</p>
              </div>
            </div>
            
            <p className="text-[11px] text-muted-foreground/70 italic">
              You can always find new matches as more researchers join the community and share their archives.
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDismissDialog(false)}
              className="border-border"
            >
              Keep Match
            </Button>
            <Button
              onClick={handleConfirmDismiss}
              disabled={isProcessing}
              className="bg-stone-700 hover:bg-muted-foreground"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Dismiss Match
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// View Summary Dialog
const ViewSummaryDialog = ({ match, open, onClose, treeId, onRequestConnection }) => {
  if (!match) return null;
  
  const typeInfo = matchTypeInfo[match.match_type] || matchTypeInfo.default;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-muted-foreground" />
            Potential Family Connection
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            A summary of this cross-tree match
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {/* Your person */}
          <div className="space-y-2">
            <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Your Ancestor</p>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary border border-border">
              <div className="w-10 h-10 rounded-full bg-border flex items-center justify-center">
                <User className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{match.your_person_name}</p>
                <p className="text-xs text-muted-foreground">From your family tree</p>
              </div>
            </div>
          </div>
          
          {/* Why this match? */}
          {match.why_suggested && (
            <div className="space-y-2">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Why This Match?</p>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary border border-border">
                <Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-[11px] text-muted-foreground/90 leading-relaxed">
                  {match.why_suggested}
                </p>
              </div>
            </div>
          )}
          
          {/* Match type */}
          <div className="space-y-2">
            <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Connection Type</p>
            <div className="p-3 rounded-lg bg-secondary border border-border">
              <p className="text-sm text-foreground/90 font-medium">{typeInfo.label}</p>
              <p className="text-xs text-muted-foreground/80 mt-0.5">{typeInfo.description}</p>
            </div>
          </div>
          
          {/* Shared signals */}
          {match.shared_signals && match.shared_signals.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Shared Signals</p>
              <SharedSignalsDisplay signals={match.shared_signals} />
            </div>
          )}
          
          {/* Score breakdown */}
          {match.score_breakdown && Object.keys(match.score_breakdown).length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Score Breakdown</p>
              <ScoreBreakdown breakdown={match.score_breakdown} />
            </div>
          )}
          
          {/* Privacy-safe description */}
          <div className="space-y-2">
            <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">What This Means</p>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary border border-border">
              <Shield className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-[11px] text-muted-foreground/80 leading-relaxed">
                {match.privacy_label}
              </p>
            </div>
          </div>
          
          {/* Match factors (fallback) */}
          {match.match_factors && Object.keys(match.match_factors).length > 0 && !match.shared_signals?.length && (
            <div className="space-y-2">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Shared Characteristics</p>
              <MatchFactors factors={match.match_factors} />
            </div>
          )}
          
          {/* Match score with confidence tier */}
          <MatchScoreIndicator score={match.match_score} confidenceTier={match.confidence_tier} />
          
          {/* Privacy notice */}
          <div className="flex items-start gap-2 p-2 rounded-lg bg-secondary border border-border">
            <Lock className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-[10px] text-muted-foreground/60 leading-relaxed">
              For privacy, personal contact details and private tree information are not shared until both parties agree to connect.
            </p>
          </div>
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          {treeId && onRequestConnection && (
            match.connection_request_sent ? (
              <Button variant="outline" disabled className="border-border text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Request Sent
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => { onRequestConnection(match); onClose(false); }}
                className="border-border"
              >
                <Link2 className="w-4 h-4 mr-2" />
                Request Connection
              </Button>
            )
          )}
          <Button variant="outline" onClick={() => onClose(false)} className="border-border">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Request Connection Dialog
const RequestConnectionDialog = ({ match, treeId, open, onClose, onSuccess }) => {
  const [requestMessage, setRequestMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!match || !treeId) return;
    setSending(true);
    try {
      await axios.post(`${API}/discovery/connection-request`, {
        tree_id: treeId,
        candidate_id: match.candidate_id,
        message: requestMessage.trim() || undefined
      });
      toast.success('Connection request sent. The researcher will be notified.');
      onSuccess?.();
      setRequestMessage('');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to send connection request');
    } finally {
      setSending(false);
    }
  };

  if (!match) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Link2 className="w-5 h-5 text-muted-foreground" />
            Request Connection
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Send a connection request to the researcher who may have documented this ancestor. 
            You can include an optional message explaining why you're interested.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="p-3 rounded-lg bg-secondary border border-border">
            <p className="text-sm font-medium text-foreground">{match.your_person_name}</p>
            <p className="text-xs text-muted-foreground">Potential match in another archive</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="request-message" className="text-foreground">Message (optional)</Label>
            <Textarea
              id="request-message"
              placeholder="e.g. I believe we may share ancestors from the same region..."
              value={requestMessage}
              onChange={(e) => setRequestMessage(e.target.value)}
              className="min-h-[80px] resize-none border-border"
              maxLength={500}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} className="border-border">
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
            Send Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main CrossTreeMatchesPanel
const CrossTreeMatchesPanel = ({ treeId, treeName }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [savingToJournalId, setSavingToJournalId] = useState(null);
  const [matches, setMatches] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [userMode, setUserMode] = useState('private');
  const [treeDiscovery, setTreeDiscovery] = useState(false);
  const [viewingMatch, setViewingMatch] = useState(null);
  const [requestingMatch, setRequestingMatch] = useState(null);

  const handleSaveToJournal = async (match) => {
    if (!treeId || !treeName) return;
    setSavingToJournalId(match.candidate_id);
    try {
      const title = `Cross-tree match: ${match.your_person_name || 'Potential connection'}`;
      await axios.post(`${API}/investigations`, {
        tree_id: treeId,
        tree_name: treeName,
        insight_id: match.candidate_id,
        insight_type: 'discovery',
        insight_title: title,
        insight_description: match.privacy_label || 'Potential family connection in another tree',
        search_params: { match_type: match.match_type, match_score: match.match_score },
        linked_individual_ids: match.your_person_id ? [match.your_person_id] : [],
        notes: null
      });
      toast.success('Discovery match added to Research Journal');
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already saved')) {
        toast.info('This match is already in your Research Journal');
      } else {
        toast.error(err.response?.data?.detail || 'Could not add to journal');
      }
    } finally {
      setSavingToJournalId(null);
    }
  };
  
  const fetchMatches = async () => {
    if (!treeId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/trees/${treeId}/cross-tree-matches`);
      setMatches(response.data.matches || []);
      setTotalMatches(response.data.total_matches || 0);
      setStatus(response.data.status || 'success');
      setMessage(response.data.message || '');
      setUserMode(response.data.user_mode || 'private');
      setTreeDiscovery(response.data.tree_discovery_enabled || false);
    } catch (error) {
      console.error('Error fetching cross-tree matches:', error);
      setStatus('error');
      setMessage('Unable to load matches at this time');
      setMatches([]);
      setTotalMatches(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatches();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when treeId changes only
  }, [treeId]);
  
  const handleViewSummary = (match) => {
    setViewingMatch(match);
  };

  const handleRequestConnection = (match) => {
    setRequestingMatch(match);
  };
  
  const handleDismiss = async (candidateId) => {
    setIsProcessing(true);
    
    try {
      await axios.post(`${API}/trees/${treeId}/cross-tree-matches/${candidateId}/dismiss`);
      
      // Remove from local state
      setMatches(prev => prev.filter(m => m.candidate_id !== candidateId));
      setTotalMatches(prev => Math.max(0, prev - 1));
      
      toast.success('Suggestion dismissed — no data was merged', { duration: 2000 });
    } catch (error) {
      console.error('Error dismissing match:', error);
      toast.error('Unable to dismiss match');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAcknowledge = async (candidateId) => {
    setIsProcessing(true);
    try {
      await axios.post(`${API}/trees/${treeId}/connection-overlaps/${candidateId}/acknowledge`);
      setMatches((prev) => prev.filter((m) => m.candidate_id !== candidateId));
      setTotalMatches((prev) => Math.max(0, prev - 1));
      toast.success('Marked as reviewed — archives were not merged', { duration: 2500 });
    } catch (error) {
      console.error('Error acknowledging overlap:', error);
      toast.error('Unable to save review');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Don't render if user is in private mode
  if (status === 'not_eligible' || userMode === 'private') {
    return null;
  }
  
  return (
    <>
      <Card className="bg-gradient-to-b from-card to-card border-secondary backdrop-blur-sm" data-testid="cross-tree-matches-panel">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CardHeader className="p-3 pb-2">
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-secondary">
                    <GitBranch className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-medium text-foreground/90">Cross-Tree Matches</h3>
                    <p className="text-[10px] text-muted-foreground/60">Potential family connections</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {totalMatches > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                      {totalMatches} {totalMatches === 1 ? 'match' : 'matches'}
                    </span>
                  )}
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          
          <CollapsibleContent>
            <CardContent className="p-3 pt-0 space-y-3">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 text-muted-foreground animate-spin mb-2" />
                  <p className="text-xs text-muted-foreground">Searching for connections...</p>
                </div>
              ) : status === 'tree_not_discoverable' ? (
                <div className="text-center py-4 space-y-2">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center mx-auto">
                    <Lock className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {message || 'Enable tree discovery to find potential connections.'}
                  </p>
                  <p className="text-[10px] text-muted-foreground/60">
                    Go to Archive settings to enable discovery.
                  </p>
                </div>
              ) : matches.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-[10px] text-muted-foreground/70">
                    These individuals in your tree may have connections to other researchers' archives.
                  </p>
                  
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                    <AnimatePresence>
                      {matches.map((match) => (
                        <MatchCard
                          key={match.candidate_id}
                          match={match}
                          treeId={treeId}
                          treeName={treeName}
                          onViewSummary={handleViewSummary}
                          onDismiss={handleDismiss}
                          onAcknowledge={handleAcknowledge}
                          onRequestConnection={handleRequestConnection}
                          onSaveToJournal={handleSaveToJournal}
                          isProcessing={isProcessing}
                          isSavingToJournal={savingToJournalId === match.candidate_id}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                  
                  {/* Privacy reminder */}
                  <div className="flex items-start gap-2 p-2 rounded-lg bg-secondary border border-border mt-3">
                    <Shield className="w-3 h-3 text-muted-foreground mt-0.5 shrink-0" />
                    <p className="text-[9px] text-muted-foreground/50 leading-relaxed">
                      Privacy protected. No personal details shared without mutual consent.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 space-y-2">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto">
                    <Sparkles className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {message || 'No cross-tree matches found yet.'}
                  </p>
                  <p className="text-[10px] text-muted-foreground/60 max-w-[200px] mx-auto">
                    As more researchers share their family archives, new connections may emerge.
                  </p>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
      
      {/* View Summary Dialog */}
      <ViewSummaryDialog
        match={viewingMatch}
        open={!!viewingMatch}
        onClose={() => setViewingMatch(null)}
        treeId={treeId}
        onRequestConnection={handleRequestConnection}
      />
      {/* Request Connection Dialog */}
      <RequestConnectionDialog
        match={requestingMatch}
        treeId={treeId}
        open={!!requestingMatch}
        onClose={() => setRequestingMatch(null)}
        onSuccess={fetchMatches}
      />
    </>
  );
};

export default CrossTreeMatchesPanel;
